import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { BrandLogo } from '@/components/branding/BrandLogo';
import { useRouteMeta } from '@/components/branding/RouteMeta';
import { useAuth } from '@/auth/AuthProvider';
import { useIdentityProvider } from '@/auth/useIdentityProvider';
import { useLoginBranding } from '@/auth/use-login-branding';
import { apiFetch } from '@/lib/api-fetch';
import { resolveLandingTarget } from '@/auth/resolveLandingTarget';

/**
 * Login page (W1-F6-S2).
 *
 * Renders a "Sign in with Microsoft" button that invokes
 * `useAuth().login()`. On success the GuestGuard redirects authenticated
 * users to `/dashboard`. In dev-bypass mode the login is instant.
 */
interface UserPreferenceRow {
  key: string;
  value: string;
}
interface PreferencesListResponse {
  items: UserPreferenceRow[];
}

export default function Login(): JSX.Element {
  const { t } = useTranslation();
  // #1716 — public route: per-route SEO title + description + canonical.
  useRouteMeta({
    title: t('seo.login.title'),
    description: t('seo.login.description'),
    canonical: true,
  });
  const { login, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // W5-F3-S3 — null means no per-partner IdP configured; fall back to the
  // platform-default multi-tenant Entra app. Used by the CTA tooltip below
  // (no visible UI here — the fallback notice was removed 2026-05-22).
  const idp = useIdentityProvider();
  const usingPartnerIdp = Boolean(idp.data?.active);
  // #1709 — white-label the pre-auth login surface from the host's
  // registered partner branding (logo + primary colour + product name).
  // Resolves to `null` while loading or when the host has no partner
  // branding, so the platform default login renders unchanged.
  const brand = useLoginBranding().data ?? null;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // W16-F5-S2 — pre-fetch the saved landing preference so the post-login
  // redirect can honour it without blocking the UI. Same query key as
  // `<UserPreferencesProvider>` so the cache is shared once the user
  // lands inside the AppShell.
  const prefs = useQuery({
    queryKey: ['user-preferences', 'all'] as const,
    queryFn: () =>
      apiFetch<PreferencesListResponse>('/api/v1/user-preferences', {
        getAccessToken,
      }),
    staleTime: 60_000,
    // Don't auto-fetch until the user clicks sign-in — saves a 401 on
    // unauthenticated page loads.
    enabled: false,
  });

  async function handleSignIn(): Promise<void> {
    setError(null);
    setLoading(true);
    try {
      await login();
      // Best-effort fetch with a 500 ms cap — we never block redirect
      // longer than that on a network call. If the refetch hasn't
      // landed by then, fall through with no saved landing — the user
      // hits the default route and the preference re-applies on the
      // next visit. A second `await prefs.refetch()` here defeated the
      // cap (TanStack dedupes to the in-flight promise) and could
      // deadlock the post-login redirect when the api was slow.
      const fetchPromise = prefs.refetch();
      const timeout = new Promise<undefined>((resolve) =>
        setTimeout(resolve, 500),
      );
      await Promise.race([fetchPromise, timeout]);
      const items = prefs.data?.items ?? [];
      const savedLanding = items.find((r) => r.key === 'defaultLanding')?.value;
      const target = resolveLandingTarget({
        location,
        savedLanding,
      });
      navigate(target, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  // Senviro #1671 — prefer the dedicated login-mark when set, fall back
  // to the in-app `logo_url` for partners that only set the avatar.
  const partnerMarkUrl = brand?.login_logo_url ?? brand?.logo_url ?? null;
  // Senviro #1671 — when the partner set a login-surface background image,
  // paint it full-bleed; the brand-gradient default takes over otherwise.
  const backgroundStyle = brand?.login_background_url
    ? {
        backgroundImage: `url("${brand.login_background_url}")`,
        backgroundSize: 'cover' as const,
        backgroundPosition: 'center' as const,
      }
    : undefined;
  const hasPartnerBackground = Boolean(brand?.login_background_url);

  return (
    <main
      className={`relative flex min-h-screen items-center justify-center px-6 py-16 ${
        hasPartnerBackground ? '' : 'bg-brand-gradient'
      }`}
      style={backgroundStyle}
      data-testid="login-page"
      data-has-partner-background={hasPartnerBackground ? 'true' : 'false'}
    >
      {hasPartnerBackground ? (
        <div
          className="absolute inset-0 bg-black/40"
          aria-hidden="true"
          data-testid="login-background-overlay"
        />
      ) : null}
      <div className="relative w-full max-w-md rounded-lg border border-[var(--color-ink-200)] bg-white p-8 shadow-sm">
        {partnerMarkUrl ? (
          <img
            src={partnerMarkUrl}
            alt={brand?.product_name ?? 'Senviro'}
            data-testid="login-partner-logo"
            data-source={brand?.login_logo_url ? 'login_logo' : 'logo'}
            className="mb-6 h-8 w-auto"
          />
        ) : (
          <BrandLogo variant="color-full" height={32} className="mb-6" />
        )}
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-ink-950)]">
          {t('auth.signIn.title', {
            appName: brand?.product_name ?? 'Senviro',
          })}
        </h1>
        <div className="mt-6 flex flex-col gap-3">
          <button
            data-testid="sign-in-entra"
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            aria-busy={loading}
            title={
              usingPartnerIdp
                ? t('auth.tooltip_partner_idp')
                : t('auth.tooltip_default_idp')
            }
            style={
              brand?.color_primary
                ? { backgroundColor: brand.color_primary }
                : undefined
            }
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-blue-500)] px-3.5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[var(--color-blue-600)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '…' : t('auth.signIn.method.entra.label')}
          </button>
        </div>
        {error ? (
          <p
            role="alert"
            className="mt-4 rounded-md border border-[var(--color-semantic-danger)] bg-white px-3 py-2 text-sm text-[var(--color-semantic-danger)]"
          >
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
